import { useState, type ReactNode } from 'react';
import type { Person } from '@domain/entities/Person';
import { Banner, Button, Field, Modal, inputClass, textareaClass } from '@presentation/design-system';
import { reasonFor } from '@presentation/format';
import { getServices } from '@infrastructure/services';
import styles from './AssignVacationAction.module.css';

/**
 * Registrar vacaciones en nombre de una persona, ya aprobadas.
 *
 * **Diez de las trescientas treinta y siete no tienen correo** y nunca van a
 * abrir el portal: limpieza, chóferes, seguridad, recepción. Su alta existe para
 * que su área les lleve la cuenta, y sin esto sus días seguirían apuntados en una
 * hoja aparte mientras el portal jura que nunca han tomado vacaciones. El caso se
 * extiende solo — quien acordó las fechas de viva voz, quien ya se fue.
 *
 * Vive en la ficha porque es una acción sobre **una persona concreta**, igual que
 * trasladarla o darla de baja. Ponerla en el asistente de solicitudes habría
 * obligado a elegir primero a quién, que es justo lo que aquí ya se sabe.
 *
 * ## Lo que este formulario no pregunta, y no es un olvido
 *
 * No pide reemplazo, aunque el módulo esté encendido: lo pide el formulario a
 * quien solicita, y quien registra en nombre de otro no sabe con quién se acordó
 * cubrirse. El servidor lo exime por lo mismo.
 *
 * Sí pide un motivo, y es obligatorio: alguien que abra su ficha y encuentre unos
 * días que no pidió merece leer por qué están ahí sin tener que preguntar.
 */

interface AssignVacationActionProps {
  readonly person: Person;
  /** El mismo par que exige el servidor: `admin` para su área, `areas` para todas. */
  readonly canAssign: boolean;
  readonly onDone: () => Promise<void>;
}

export const AssignVacationAction = ({
  person,
  canAssign,
  onDone,
}: AssignVacationActionProps): ReactNode => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ folio: string; businessDays: number; owedDays: number } | null>(
    null,
  );

  const close = (): void => {
    setOpen(false);
    setStartDate('');
    setEndDate('');
    setReason('');
    setError(null);
    setDone(null);
  };

  const listo =
    startDate.length > 0 && endDate.length > 0 && reason.trim().length > 0 && endDate >= startDate;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await getServices().requests.assign({
      personId: person.id,
      startDate,
      endDate,
      reason: reason.trim(),
    });
    setBusy(false);

    if (!result.ok) {
      setError(`No se pudo registrar.${reasonFor(result.error)}`);
      return;
    }
    setDone(result.value);
    await onDone();
  };

  if (!canAssign || person.status !== 'active') return null;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        Registrar vacaciones
      </Button>

      {open && (
        <Modal
          title={`Registrar vacaciones a ${person.displayName}`}
          onClose={close}
          footer={
            done !== null ? (
              <Button variant="primary" onClick={close}>
                Cerrar
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={close}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  disabled={busy || !listo}
                  onClick={() => {
                    void save();
                  }}
                >
                  {busy ? 'Registrando…' : 'Registrar como aprobadas'}
                </Button>
              </>
            )
          }
        >
          <div className={styles.body}>
            {done !== null ? (
              <>
                <Banner tone="success">
                  Registradas con folio {done.folio}: {done.businessDays}{' '}
                  {done.businessDays === 1 ? 'día hábil' : 'días hábiles'} descontados de su saldo.
                </Banner>
                {/* Lo que no alcanzaba se registró igual: que se lea quién lo debe y cuánto. */}
                {done.owedDays > 0 && (
                  <Banner tone="warn">
                    No le alcanzaban los días: {person.displayName} queda debiendo {done.owedDays}{' '}
                    {done.owedDays === 1 ? 'día' : 'días'}, que se descuentan de su siguiente periodo.
                  </Banner>
                )}
              </>
            ) : (
              <>
                {error !== null && <Banner tone="danger">{error}</Banner>}

                <Banner tone="info">
                  Entran ya aprobadas y se descuentan de su saldo. Nadie más tiene que
                  darles el visto bueno. Si no le alcanzan los días se registran igual, y
                  queda debiendo los que falten.
                </Banner>

                <div className={styles.dates}>
                  <Field label="Primer día">
                    {(id) => (
                      <input
                        id={id}
                        type="date"
                        className={inputClass()}
                        value={startDate}
                        onChange={(event) => {
                          setStartDate(event.target.value);
                          setError(null);
                        }}
                      />
                    )}
                  </Field>
                  <Field
                    label="Último día"
                    error={
                      endDate.length > 0 && endDate < startDate
                        ? 'El último día es anterior al primero.'
                        : null
                    }
                  >
                    {(id) => (
                      <input
                        id={id}
                        type="date"
                        className={inputClass()}
                        value={endDate}
                        onChange={(event) => {
                          setEndDate(event.target.value);
                          setError(null);
                        }}
                      />
                    )}
                  </Field>
                </div>

                {/* Se admiten fechas pasadas a propósito: casi siempre se registra
                    lo que ya ocurrió. El servidor corta al año, para que un dedazo
                    no mande unas vacaciones al siglo pasado. */}
                <Field
                  label="Por qué se registra"
                  hint="Queda en la solicitud y en la bitácora. Lo leerá quien abra su ficha."
                >
                  {(id) => (
                    <textarea
                      id={id}
                      className={textareaClass()}
                      rows={3}
                      placeholder="Acordadas con su jefe directo el 12 de agosto; no tiene acceso al portal."
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value);
                        setError(null);
                      }}
                    />
                  )}
                </Field>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
